import { useMemo, useState } from "react";
import {
  Avatar,
  AvatarFallback,
  Icon,
  TooltipContent,
  TooltipTrigger,
  Tooltip,
} from "@elaniintc/goes-design-system";
import { cn } from "@/utils/theme";

type TooltipTone = "action" | "warning";
type IconStyle = "far" | "fas";

export interface UsersListItem {
  id: string;
  title: string;
  description: string;
  icon: {
    name: string;
    type?: IconStyle;
    tone?: TooltipTone;
  };
}

export interface UsersListTooltipProps {
  items: UsersListItem[];
  placeholder?: string;
  className?: string;
}

const normalizeSearch = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const filterItems = (items: UsersListItem[], query: string) => {
  const normalizedQuery = normalizeSearch(query);
  if (!normalizedQuery) {
    return items;
  }

  return items.filter((item) => {
    const haystack = normalizeSearch(`${item.title} ${item.description}`);
    return haystack.includes(normalizedQuery);
  });
};

const getInitials = (value: string) => {
  const word = value.trim().split(/\s+/).find(Boolean);
  if (!word) {
    return "";
  }
  return word.charAt(0).toUpperCase();
};

const MAX_TRIGGER_ITEMS = 3;

const SearchField = ({
  placeholder,
  value,
  onChange,
}: {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange(event.target.value);

  return (
    <div className="w-full border-b border-neutral px-2 py-2">
      <div className="flex items-center gap-2 rounded-lg border border-neutral px-2 py-1">
        <Icon type="far" name="search" className="text-sm text-body" />
        <input
          type="text"
          placeholder={placeholder}
          className="w-full bg-transparent text-sm leading-6 text-body placeholder:text-body-quiet focus:outline-none"
          value={value}
          onChange={handleChange}
        />
      </div>
    </div>
  );
};

const ItemRow = ({ item }: { item: UsersListItem }) => {
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <Avatar
        key={item.id}
        size="sm"
        shape="circle"
        color={item?.icon.tone || "action"}
      >
        <AvatarFallback
          type="icon"
          shape="circle"
          color={item?.icon.tone || "action"}
        >
          <Icon type={item.icon.type || "far"} name={item.icon.name} />
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="truncate text-base font-semibold leading-7 text-body">
          {item.title}
        </p>
        <p className="text-sm font-normal leading-6 wrap-break-word text-body-quiet">
          {item.description}
        </p>
      </div>
    </div>
  );
};

const ItemsList = ({ items }: { items: UsersListItem[] }) => {
  if (items.length === 0) {
    return (
      <div className="px-3 py-6 text-center text-sm text-body-quiet">
        Sin resultados
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {items.map((item) => (
        <ItemRow key={item.id} item={item} />
      ))}
    </div>
  );
};

const TriggerAvatars = ({ items }: { items: UsersListItem[] }) => {
  if (items.length === 0) {
    return null;
  }

  const visibleItems = items.slice(0, MAX_TRIGGER_ITEMS);
  return (
    <div className="flex items-center -space-x-2">
      {visibleItems.map((item, index) => (
        <span
          className="relative bg-white rounded-full"
          key={item.id}
          style={{ zIndex: visibleItems.length - index }}
        >
          <Avatar size="xs" shape="circle" color={item?.icon.tone || "action"}>
            <AvatarFallback
              type="icon"
              shape="circle"
              color={item?.icon.tone || "action"}
            >
              {getInitials(item.title)}
            </AvatarFallback>
          </Avatar>
        </span>
      ))}
    </div>
  );
};

export const UsersListTooltip = ({
  items = [],
  placeholder = "Buscar...",
  className,
}: UsersListTooltipProps) => {
  const [searchValue, setSearchValue] = useState("");

  const resetSearch = () => {
    setSearchValue("");
  };

  const handleOpenChange = () => {
    resetSearch();
  };

  const filteredItems = useMemo(
    () => filterItems(items, searchValue),
    [items, searchValue],
  );

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  return (
    <Tooltip onOpenChange={handleOpenChange}>
      {/* Content Box */}
      <TooltipContent className="p-0">
        <div
          role="none"
          className={cn("w-full max-h-71 max-w-55 overflow-hidden", className)}
          onClick={(event) => event.stopPropagation()}
        >
          <SearchField
            placeholder={placeholder}
            value={searchValue}
            onChange={handleSearchChange}
          />
          <div className="hidden-scrollbar flex max-h-59 flex-col overflow-y-auto">
            <ItemsList items={filteredItems} />
          </div>
        </div>
      </TooltipContent>

      {/* Trigger Item */}
      <TooltipTrigger
        aria-label="Ver listado"
        className="flex items-center gap-2 rounded-md px-2 py-1"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="sr-only">Ver listado</span>
        <TriggerAvatars items={items} />
      </TooltipTrigger>
    </Tooltip>
  );
};
